/**
 * Persistence for the pre-0.3 legacy-backup reissue offer (OPL-2518).
 *
 * When a legacy backup is upgraded at sign-in, the old file becomes obsolete
 * (the identities get new bapIds) and the user MUST save the freshly issued
 * encrypted backup. The login page raises this offer during decryption, but
 * the flow then hard-navigates to /onboarding or /account, which tears down
 * the login page (and any sonner toast it raised). A real user on a fast
 * connection never sees the offer.
 *
 * The durable fix: persist the reissued backup to sessionStorage here, then
 * render a persistent, dismissible banner on the destination pages (onboarding
 * and account) until the user has saved the file. sessionStorage survives the
 * hard navigation within the same tab, so the offer is reachable where the
 * user actually dwells.
 */

import { STORAGE_KEYS } from "@/lib/storage-keys";

export interface ReissuedBackupOffer {
	/** The re-encrypted, upgraded backup string (password-encrypted). */
	encrypted: string;
	/** Suggested filename for the reissued backup file. */
	filename: string;
	/** Number of identities that were recomputed to the current derivation. */
	recomputedCount: number;
	/** Public old->new BAP ID mapping from the derivation reissue. */
	mappings?: Array<{
		oldBapId: string;
		newBapId: string;
		rootPath?: string;
	}>;
}

/**
 * Persist a reissued-backup offer so the destination page can surface a
 * download banner after the post-sign-in navigation.
 */
export function persistReissuedBackupOffer(offer: ReissuedBackupOffer): void {
	if (typeof window === "undefined") {
		return;
	}
	sessionStorage.setItem(STORAGE_KEYS.REISSUED_BACKUP, JSON.stringify(offer));
}

/**
 * Read the pending reissued-backup offer, or null when none is pending.
 */
export function readReissuedBackupOffer(): ReissuedBackupOffer | null {
	if (typeof window === "undefined") {
		return null;
	}
	const raw = sessionStorage.getItem(STORAGE_KEYS.REISSUED_BACKUP);
	if (!raw) {
		return null;
	}
	try {
		const parsed = JSON.parse(raw) as Partial<ReissuedBackupOffer>;
		if (
			typeof parsed.encrypted === "string" &&
			typeof parsed.filename === "string"
		) {
			return {
				encrypted: parsed.encrypted,
				filename: parsed.filename,
				recomputedCount:
					typeof parsed.recomputedCount === "number"
						? parsed.recomputedCount
						: 0,
				mappings: Array.isArray(parsed.mappings)
					? parsed.mappings
							.filter(
								(mapping) =>
									mapping &&
									typeof mapping === "object" &&
									typeof (
										mapping as {
											oldBapId?: unknown;
										}
									).oldBapId === "string" &&
									typeof (
										mapping as {
											newBapId?: unknown;
										}
									).newBapId === "string"
							)
							.map((mapping) => {
								const typed = mapping as {
									oldBapId: string;
									newBapId: string;
									rootPath?: unknown;
								};
								return {
									oldBapId: typed.oldBapId,
									newBapId: typed.newBapId,
									rootPath:
										typeof typed.rootPath === "string"
											? typed.rootPath
											: undefined,
								};
							})
					: undefined,
			};
		}
	} catch {
		// Corrupt entry — drop it so the banner doesn't get stuck.
	}
	clearReissuedBackupOffer();
	return null;
}

/**
 * Clear the pending reissued-backup offer (after the user saves or dismisses).
 */
export function clearReissuedBackupOffer(): void {
	if (typeof window === "undefined") {
		return;
	}
	sessionStorage.removeItem(STORAGE_KEYS.REISSUED_BACKUP);
}
